import useSession from '#/common/hooks/useSession';
import storage from '#/common/utils/storage';
import { Tenant } from '#/graphql/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import Modal from '../Modal';
import TMMButton from '../TMMButton';
import SelectCard from '../SelectCard';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { color } from '@nuvolo/nuux-theme/utils';
import FAIcon from '#/common/components/FAIcon';

export interface TenantSelectorProps {
  onTenantSelect?: (tenant: Tenant) => void;
  titleIconName?: string;
  sectionLabel?: string;
}

const CardContainer = styled('div')<{ isDesktop?: boolean }>(({ isDesktop }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px 16px',
  justifyContent: isDesktop ? 'flex-start' : 'center',

  '& > div:not(.section-label)': {
    flex: isDesktop ? '0 0 calc(50% - 8px)' : '1 1 100%',
    minWidth: isDesktop ? 'calc(50% - 8px)' : '100%',
  },
}));

const ModalTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const IconHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  borderRadius: '50%',
  backgroundColor: color('custom', 'defaultIconBackground'),

  '& > svg': {
    fontSize: '24px',
    color: color('primary', 'dark'),
  },
});

const SectionLabel = styled('div')({
  width: '100%',
  fontSize: '14px',
  fontWeight: 500,
  color: color('custom', 'constantGray'),
  marginBottom: '4px',
});

const store = storage(sessionStorage);

const TenantSelector: React.FC<TenantSelectorProps> = (props) => {
  const { onTenantSelect, titleIconName = 'buildings', sectionLabel = 'Organizations' } = props;
  const { isDesktop } = useViewport();

  const { tenants: tenantsData, user } = useSession();

  const tenants = useMemo(() => {
    return tenantsData?.map((t) => t.tenant) ?? [];
  }, [tenantsData]);

  const storeKey = `${user?.id ?? '?'}/selected-tenant`;
  const defaultTenantId = store.get<string>(storeKey);
  const defaultTenant = tenants.find((t) => t.id === defaultTenantId);

  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const selectTenant = useCallback(
    (tenant: Tenant) => {
      store.set(storeKey, tenant.id);
      if (onTenantSelect) {
        onTenantSelect(tenant);
      }
      setSelectedTenant(tenant);
    },
    [onTenantSelect, storeKey],
  );

  // only 1 tenant available
  useEffect(() => {
    if (tenants?.length === 1) {
      selectTenant(tenants[0]);
    }
  }, [selectTenant, tenants]);

  // current tenant selected via card
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(defaultTenant?.id);

  const handleCardClick = useCallback((tenantId: string) => {
    setSelectedTenantId(tenantId);
  }, []);

  const handleNext = useCallback(() => {
    const tenant = tenants.find((t) => t.id === selectedTenantId);
    if (tenant) {
      selectTenant(tenant);
    }
  }, [selectedTenantId, tenants, selectTenant]);

  const navigate = useNavigate();
  const cancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (selectedTenant) {
    return <></>;
  }

  return (
    <Modal
      visible
      title={
        <ModalTitle>
          <IconHolder>
            <FAIcon icon={titleIconName} size="sm" />
          </IconHolder>
          <span>Choose Organization</span>
        </ModalTitle>
      }
      dragEnabled
      width={isDesktop ? 700 : 550}
      onHiding={cancel}
      height={450}
      content={
        <CardContainer isDesktop={isDesktop}>
          <SectionLabel className="section-label">{sectionLabel}</SectionLabel>
          {tenants.map((tenant) => (
            <SelectCard
              key={tenant.id}
              id={tenant.id}
              iconName="buildings"
              title={tenant.name || 'Unnamed Organization'}
              subtitle={tenant.address || undefined}
              selected={selectedTenantId === tenant.id}
              onClick={handleCardClick}
              iconColor={color('custom', 'constantBlue')}
              iconHolderOpacity={0.2}
            />
          ))}
        </CardContainer>
      }
      actions={[
        <TMMButton
          className="width-50"
          key="cancel"
          label="Cancel"
          type="normal"
          onClick={cancel}
        />,
        <TMMButton
          className="width-50"
          key="ok"
          label="Next"
          type="submit"
          disabled={!selectedTenantId}
          onClick={handleNext}
        />,
      ]}
    />
  );
};

export default React.memo(TenantSelector);
